/* -----------------------------------------------------------------------------------------------------
  - Created: [13-02-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD] 
  - Description: This file contains employee list table
  ------------------------------------------------------------------------------------------------------- */

import React, { useEffect, useState } from 'react';
import WorkforceSearchFilter from '../WorkforceSearch/WorkforceSearchFilter';
import CustomTable from '../../../../Components/custom_components/CustomTable/CustomTable';
import CustomBottomPagination from '../../../../Components/custom_components/CustomBottomPagination/CustomBottomPagination';
import CustomModal from '../../../../Components/custom_components/CustomModal/CustomModal';
import { get_workforce_with_pagination } from '../../../../config/Resource/workforceApis';
import { handlePaginationEntries } from '../../../../utils/pagination';
import { useNavigate } from 'react-router-dom';
import { getUserData } from '../../../../utils/localStorage';
import { useSelector } from 'react-redux';
import { hasPrivilege } from '../../../../utils/privileges';
import Loader from '../../../../Components/Loader/Loader';
import './WorkforceTableStyle.css';
import { Icons } from '../../../../utils/GlobalIcons';
import ActionHistory from '../../../../Components/ActionHistory/ActionHistory';
import PasswordReset from '../PasswordReset/PasswordReset';
import CustomTooltipWithoutRoundBorder from '../../../../Components/CustomActionButtonWithTooltip/CustomTooltipWithoutRoundBorder';
import { getStatusColors } from '../../../../utils/constants';
import { fetchHistoryData } from '../../../../config/HistoryService/historyService';


function WorkforceTable() {
    const userData = getUserData();
    const privileges = useSelector((state) => state.privileges.privileges);
    const workforceFilters = useSelector(state => state.workforceFilters.workforceFilters);

    const navigate = useNavigate();

    // State variables for managing project data, pagination, and modal states
    const [Listing, SetListing] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalItemPerPage, setTotalItemPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const [pagination, setPagination] = useState({ start: 0, end: 0, totalEntries: 0 });

    const [isLoading, setIsLoading] = useState(false); // Loader state
    // State for Change Password modal visibility
    const [showChangePassword, setShowChangePassword] = useState({ isOpen: false, userId: null });

    const [showHistory, setShowHistory] = useState(false); // history modal state
    const [historyData, setHistoryData] = useState([]);
    const [historyLoader, setHistoryLoader] = useState(false); // Loader state

    // Fetch project data on page change or refresh
    useEffect(() => {
        fetchWorkforce();
    }, [pageNumber, totalItemPerPage, workforceFilters]);

    // Fetch project data from API with filter values
    const fetchWorkforce = async () => {
        const formData = new FormData();
        formData.append('page', pageNumber);
        formData.append('pageItemcount', totalItemPerPage);

        // Append filter values to the formData 
        Object.keys(workforceFilters).forEach(key => {
            formData.append(key, workforceFilters[key]);
        });

        try {
            setIsLoading(true)
            const response = await get_workforce_with_pagination(formData);
            if (response?.status === "success") {
                const data = response.data;
                SetListing(data.data);
                setTotalPages(data.last_page);
                setPagination(handlePaginationEntries(data.from, data.to, data.total));
            } else {
                SetListing([]);
            }
        } catch (error) {
            SetListing([]);
        } finally {
            setIsLoading(false)
        }
    };


    // Map project data to rows for the table
    const rows = () => Listing?.map((item, index) => ({
        key: pageNumber * totalItemPerPage - (totalItemPerPage - (index + 1)),
        id: item.UD_ID,
        UD_name: item.UD_name,
        UD_OptimaNo: item.UD_OptimaNo,
        UD_email: item.UD_email,
        UD_OfficialMobile: item.UD_OfficialMobile,
        UD_PassportNo: item.UD_PassportNo,
        UD_Nationality: item.UD_Nationality,
        UD_status: item.UD_status,
        role: item.RoleName,
        designation: item.DesignationName,
        Classification: item.CalssificationName,
        UD_HSE_Passport: item.UD_HSE_Passport


    }));


    // Navigate to Add DailyLog page
    const handleNavigate = (workforceId) => {
        navigate(`/resources/workforce/edit/${userData?.RoleId}/${workforceId}`, { replace: true });

    };


    // Navigate to workforce view page
    const handleViewNavigate = (workforceId) => {
        navigate(`/resources/workforce/view/${workforceId}`, { replace: true });

    };

    // Define the required permission actions
    const PERMISSION_ACTIONS = {
        view: 4,
        edit: 5,
        history: 6,
        add: 3,
        changePassword: 8
    };

    // Map permissions to boolean values based on privileges
    const userPermissions = {
        view: hasPrivilege(privileges, "Resources", 6, PERMISSION_ACTIONS.view),
        edit: hasPrivilege(privileges, "Resources", 6, PERMISSION_ACTIONS.edit),
        history: hasPrivilege(privileges, "Resources", 6, PERMISSION_ACTIONS.history),
        add: hasPrivilege(privileges, "Resources", 6, PERMISSION_ACTIONS.add),
        changePassword: hasPrivilege(privileges, "Resources", 6, PERMISSION_ACTIONS.changePassword),
    };
    // / Function to check if any action-related permissions are granted
    const hasActionPermissions = userPermissions.view || userPermissions.edit || userPermissions.history || userPermissions.changePassword;

    const columns = [
        { title: "#", key: "key" },
        { title: 'Name', key: 'UD_name' },
        { title: 'Role', key: 'role' },
        { title: 'Classification', key: 'Classification' },
        { title: 'Designation', key: 'designation' },
        { title: 'Optima No', key: 'UD_OptimaNo' },
        { title: 'Email', key: 'UD_email' },
        { title: 'Official Mobile', key: 'UD_OfficialMobile' },
        { title: 'Passport No', key: 'UD_PassportNo' },
        { title: 'HSE Passport No', key: 'UD_HSE_Passport' },
        { title: 'Nationality', key: 'UD_Nationality' },
        {
            title: "Status",
            key: "UD_status",
            render: (_, record) => {
                //  actual status text
                const statusText = record?.UD_status || "Unknown";

                const statusCode = statusText.toLowerCase() === "active" ? 1 : statusText.toLowerCase() === "inactive" ? 2 : '';
                const style = getStatusColors(statusCode);

                return (
                    <span className="status-badge" style={style}>
                        {statusText}
                    </span>
                );
            },
        },

        { title: "Actions", key: "actions", render: (text, row) => customActions(row) },
    ].filter(column => column.key !== "actions" || hasActionPermissions); //removes the "Actions" column when hasActionPermissions is false

    // Render custom actions based on user role
    const customActions = (row) => {
        return (
            <div className='action-container'>
                {userPermissions.view &&
                    <CustomTooltipWithoutRoundBorder title="View ">
                        <Icons.View className="view-action-icon" onClick={() => handleViewNavigate(row?.id)} />
                    </CustomTooltipWithoutRoundBorder>
                }
                {userPermissions.edit &&
                    <CustomTooltipWithoutRoundBorder title="Edit">
                        <Icons.Edit className="edit-action-icon" onClick={() => handleNavigate(row?.id)} />
                    </CustomTooltipWithoutRoundBorder>
                }

                {userPermissions.changePassword &&
                    <CustomTooltipWithoutRoundBorder title="Change Password">
                        <Icons.Password className="password-change-action-icon" onClick={() => handleResetPassModal(true, row?.id)} />
                    </CustomTooltipWithoutRoundBorder>
                }
                {userPermissions.history && <CustomTooltipWithoutRoundBorder title="History">
                    <Icons.History className="history-action-icon" onClick={() => handleHistoryModal(true, row?.id)} />
                </CustomTooltipWithoutRoundBorder>}
            </div>
        )
    };


    // Handle resetpassword Modal state
    const handleResetPassModal = (value, userId) => {
        setShowChangePassword({ isOpen: value, userId: userId });
    }

    // Handle pagination changes
    const handlePageChange = (pageNumber) => setPageNumber(pageNumber);
    const handleItemPageCountChange = (pageItemCount) => setTotalItemPerPage(pageItemCount);


    // HISTORY BUTTON CLICK AND API CALL
    const handleHistoryModal = async (open, id) => {
        setShowHistory(open);

        if (open) {
            setHistoryLoader(true);
            try {

                const response = await fetchHistoryData('workforce', id);
                if (response?.status === "success") {

                    //  Transform API response for  table usage
                    const formatted = response?.data?.map((item, index) => ({
                        id: item.id,
                        name: item.workforce_name || "-",
                        changedBy: item.changed_by_name || "Unknown",
                        changedAt: item.changed_at || "-",
                        changeDescription: item.change_description || "-",
                    }));

                    setHistoryData(formatted || []);
                } else {
                    // InfoToast("No history data found.");
                    setHistoryData([]);
                }
            } catch (error) {
                console.error("Error fetching project history:", error);
            } finally {
                setHistoryLoader(false);
            }
        } else {
            setHistoryData([]);
        }
    };

    return (
        <div className='employee-table-container'>
            <WorkforceSearchFilter
                tableTitle="Workforce"
                isAddPermission={userPermissions?.add}

                on_change_value={handlePageChange}
                on_page_count_update={handleItemPageCountChange}

            />

            <CustomTable
                columns={columns}
                rows={isLoading ? [] : rows()}
                renderActions={customActions}
                isLoading={isLoading}
            />

            <div className='bottom-container'>
                {isLoading ? <Loader /> : <CustomBottomPagination
                    items_per_page={totalItemPerPage}
                    current_page={pageNumber}
                    total_page_count={totalPages}
                    on_change_value={handlePageChange}
                    on_page_count_update={handleItemPageCountChange}
                    pagination={pagination}
                />}
            </div>

            {/* HISTORY VIEW MODAL */}
            {showHistory && <CustomModal
                open={showHistory}
                handleClose={() => handleHistoryModal(false)}
                title="Workforce History"
                maxWidth="75%"
                body={
                    historyLoader ? (
                        <div style={{ textAlign: "center", padding: "20px" }}>
                            <Loader />
                        </div>
                    ) : (
                        <ActionHistory
                            title=""
                            data={historyData}
                            columns={[
                                { key: "name", label: "Workforce Name" },
                                { key: "changedBy", label: "Changed By" },
                                { key: "changedAt", label: "Changed At" },
                                { key: "changeDescription", label: "Change Description" },
                            ]}
                            noDataMessage="No change history available."
                        />

                    )
                }
            />}


            {/* Modal for RESET Password */}
            {showChangePassword?.isOpen && <CustomModal
                open={showChangePassword?.isOpen}
                title={"Reset Password"}
                handleClose={() => handleResetPassModal(false, null)}
                body={
                    <PasswordReset
                        userId={showChangePassword?.userId}
                        handleClose={() => handleResetPassModal(false, null)}
                    />
                }

            />}
        </div>
    );
}

export default WorkforceTable;
